import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import { selectIsEditMode } from 'features/common/editModeSlice';
import _ from 'lodash';
import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, FormControl, Modal, ModalProps, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FoodSystemMenu, MenuOrderInMenuGroup, SearchMenuOrderInMenuGroup, SubMenuGroup } from '../models/submenulist';
import { subMenuGroupActions } from '../subMenuSlice';
import './SubMenuList.scss';

const DefaultSettingModal = forwardRef<
  ModalProps,
  {
    subMenuGroup: SubMenuGroup | undefined;
    menuOrders: MenuOrderInMenuGroup[] | undefined;
    handleSetFoodSystemMenu: (foodSystemMenu: FoodSystemMenu | undefined) => void;
    handleSetDefaultSettingFlag?: (subMenuGroup: SubMenuGroup) => void;
    onClose?: () => void;
  }
>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // states
  const [showModal, setShowModal] = useState(false);
  const [subMenuOrders, setSubMenuOrders] = useState<Array<MenuOrderInMenuGroup>>([]);
  const [subMenuGroup, setSubMenuGroup] = useState<SubMenuGroup>();
  const [searchMenuOrderInMenuGroup, setSearchMenuOrderInMenuGroup] = useState<SearchMenuOrderInMenuGroup>();
  const [defaultSettingFlag, setDefaultSettingFlag] = useState({ flag: false, subMenuGroupId: -1 });

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  useEffect(() => {
    if (props.menuOrders !== undefined) {
      setSubMenuOrders(props.menuOrders);
    }
  }, [props.menuOrders]);

  useEffect(() => {
    if (props.subMenuGroup !== undefined) {
      setSubMenuGroup(props.subMenuGroup);
    }
  }, [props.subMenuGroup]);

  useEffect(() => {
    if (props.subMenuGroup !== undefined && subMenuOrders !== undefined && subMenuOrders.length > 0) {
      var minOrder = _.minBy(subMenuOrders, 'order');
      const menu = props.subMenuGroup.sub_menus.find((menu) => menu.id === minOrder?.sub_menu_id);
      props.handleSetFoodSystemMenu(menu?.food_system_menu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMenuOrders]);

  useEffect(() => {
    if (subMenuGroup && subMenuGroup.sub_menus.length > 0) {
      let menu_ids = subMenuGroup.sub_menus.map((obj) => obj.id);
      const obj = {
        menu_ids: menu_ids,
        sub_menu_group_id: subMenuGroup.id,
      } as SearchMenuOrderInMenuGroup;

      setSearchMenuOrderInMenuGroup(obj);

      if (defaultSettingFlag.subMenuGroupId > 0 && defaultSettingFlag.subMenuGroupId === subMenuGroup.id) {
        setDefaultSettingFlag({
          ...defaultSettingFlag,
          flag: defaultSettingFlag.flag,
          subMenuGroupId: subMenuGroup.id,
        });
      } else {
        setDefaultSettingFlag({
          ...defaultSettingFlag,
          flag: subMenuGroup.default_setting_flg,
          subMenuGroupId: subMenuGroup.id,
        });
      }
    } else {
      props.handleSetFoodSystemMenu(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMenuGroup]);

  useEffect(() => {
    if (searchMenuOrderInMenuGroup) {
      dispatch(subMenuGroupActions.fetchOrderMenuInGroup(searchMenuOrderInMenuGroup));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchMenuOrderInMenuGroup)]);

  useImperativeHandle(ref, () => ({
    closeModal() {
      setShowModal(false);
    },
    openModal() {
      setShowModal(true);
    },
  }));

  const handleChangePriority = (id: number, newPriority: number) => {
    const updatedSubMenuOrders = subMenuOrders.map((obj) => {
      if (obj.id === id) {
        return { ...obj, order: newPriority };
      }
      return obj;
    });
    setSubMenuOrders(updatedSubMenuOrders);
  };

  const handleChangeDefaultSettingFlag = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.checked;
    if (subMenuOrders) {
      const newState = subMenuOrders.map((obj) => {
        return { ...obj, default_setting_flg: value };
      });
      setSubMenuOrders(newState);
    }
    if (subMenuGroup) {
      const newSubMenuGroup = { ...subMenuGroup, default_setting_flg: value };
      setSubMenuGroup(newSubMenuGroup);
      setDefaultSettingFlag({ ...defaultSettingFlag, flag: value, subMenuGroupId: subMenuGroup.id });
      if (props.handleSetDefaultSettingFlag) {
        props.handleSetDefaultSettingFlag(newSubMenuGroup);
      }
    }
  };

  const updateSubmenuOrderGroup = () => {
    if (subMenuOrders && subMenuOrders.length > 0) {
      dispatch(subMenuGroupActions.patchSubMenuGroupOrder(subMenuOrders));
      const sorted = _.sortBy(subMenuOrders, 'order');
      setSubMenuOrders(sorted);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    props.onClose?.();
  };

  return (
    <Modal
      dialogClassName="form-submenu-list"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={showModal}
      backdrop={'static'}
      onHide={handleCloseModal}
    >
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('SubMenuGroupListFeature.Table.default_selection')} />
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <div className="mb-3 text-end">
          <button className="btn btn--dark-green" type="button" onClick={updateSubmenuOrderGroup}>
            {t('SubMenuGroupListFeature.Component.save')}
          </button>
        </div>
        <Row className="mb-3 align-items-center">
          <Col className="text-end">
            <div> {t('SubMenuGroupListFeature.Text.default_configuration')}</div>
          </Col>
          <Col>
            <div className="switchToggle">
              <FormControl
                type="checkbox"
                defaultChecked={defaultSettingFlag.flag}
                onChange={handleChangeDefaultSettingFlag}
                id="switch"
              ></FormControl>
              <label htmlFor="switch">Toggle</label>
            </div>
          </Col>
        </Row>
        <Table className="table table-bordered text-center align-middle mb-3">
          <thead>
            <tr>
              <th style={{ width: '60%' }}>{t('SubMenuGroupListFeature.Text.submenu_name')}</th>
              <th style={{ width: '40%' }}>{t('SubMenuGroupListFeature.Text.setting_priority_order')}</th>
            </tr>
          </thead>
          <tbody>
            {subMenuOrders &&
              subMenuOrders.map((menu_order, index) => {
                let menu = subMenuGroup?.sub_menus?.find((menuItem) => menuItem.id === menu_order.sub_menu_id);
                return (
                  <tr key={index}>
                    <th className="table-primary">{menu?.food_system_menu?.name}</th>
                    <td>
                      <FormControl
                        className="text-center"
                        type="number"
                        min="1"
                        max="99999"
                        id={menu_order.id.toString()}
                        onChange={(e) => handleChangePriority(menu_order.id, +e.target.value)}
                        defaultValue={menu_order.order}
                      ></FormControl>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </Modal.Body>
    </Modal>
  );
});

export default DefaultSettingModal;
